import { FC } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { BsCalendarXFill } from "react-icons/bs";
import { FaCalendarCheck } from "react-icons/fa";
import { Spinner } from "reactstrap";

import { Popover } from "@components/shared/Popover/Popover";
import { NextAvailabilityPopoverProps } from "@models/components/amenity.model";

export const NextAvailabilityPopover: FC<NextAvailabilityPopoverProps> = ({
  amenityTitle,
  className,
  container,
  fade = false,
  isLoading,
  isOpen,
  modifiers,
  nextAvailableDatetime,
  noAvailability,
  onClose,
  placement = "auto",
  target,
}) => {
  const { i18n, t } = useTranslation();

  const formattedDate = nextAvailableDatetime
    ? moment(nextAvailableDatetime)
        .locale(i18n.language || "en")
        .format("dddd, D MMMM YYYY")
    : "";
  const formattedTime = nextAvailableDatetime
    ? moment(nextAvailableDatetime).format("HH:mm")
    : "";

  return (
    <Popover
      target={target}
      container={container}
      fade={fade}
      isOpen={isOpen}
      modifiers={modifiers}
      onClose={onClose}
      placement={placement}
      title={t("amenityNextAvailability.modalTitle")}
      className={`fg-next-availability-popover ${className || ""}`}
      testId="next-availability-popover"
    >
      {isLoading ? (
        <div
          className="text-center py-2"
          data-testid="next-availability-loading"
        >
          <div className="fg-next-avail-icon-circle">
            <Spinner size="sm" color="primary" />
          </div>
          {amenityTitle && (
            <div className="fg-next-avail-title">{amenityTitle}</div>
          )}
          <p className="fg-next-avail-desc">
            {t("amenityNextAvailability.checkingAvailability")}
          </p>
        </div>
      ) : noAvailability ? (
        <div
          className="text-center py-1"
          data-testid="next-availability-not-found"
        >
          <div className="fg-next-avail-icon-circle warning">
            <BsCalendarXFill size={26} />
          </div>
          {amenityTitle && (
            <div className="fg-next-avail-title">{amenityTitle}</div>
          )}
          <p
            className="fg-next-avail-desc"
            data-testid="no-availability-message"
          >
            {t("amenityNextAvailability.noAvailabilityFound")}
          </p>
        </div>
      ) : nextAvailableDatetime ? (
        <div
          className="text-center py-1"
          data-testid="next-availability-success"
        >
          <div className="fg-next-avail-icon-circle success">
            <FaCalendarCheck size={24} />
          </div>
          {amenityTitle && (
            <div className="fg-next-avail-title">{amenityTitle}</div>
          )}
          <div className="p-2 rounded-3 border mt-2 text-start fg-next-availability-popover-slot-msg-wrapper">
            <span
              className="text-muted small text-uppercase fw-semibold d-block"
              style={{ fontSize: "0.7rem" }}
            >
              {t("amenityNextAvailability.availableSlotFound")}
            </span>
            <span
              className="fw-bold text-dark d-block mt-1"
              style={{ fontSize: "0.875rem" }}
              data-testid="next-available-date"
            >
              {formattedDate}
            </span>
            <span
              className="fg-next-availability-popover-slot-msg-time fw-semibold d-block"
              style={{ fontSize: "0.8rem" }}
              data-testid="next-available-time"
            >
              {t("amenityNextAvailability.time")}: {formattedTime}
            </span>
          </div>
        </div>
      ) : null}
    </Popover>
  );
};

export default NextAvailabilityPopover;
